import type { CognitoUserSession } from "amazon-cognito-identity-js";
import { AuthenticationDetails, CognitoUser, CognitoUserAttribute, CognitoUserPool } from "amazon-cognito-identity-js";
import Flags from "common/constants/flags";

export const HIGHARC_USER_COOKIE = "_higharc_user";
export const AUTH_ERRORS = {
  RATE_LIMIT_EXCEEDED: "LimitExceededException",
  USER_ALREADY_EXSITS: "UsernameExistsException",
  INVALID_PARAMETER: "InvalidParameterException",
  INVALID_CONFIRMATION_CODE: "CodeMismatchException",
  INVALID_PASSWORD: "InvalidPasswordException",
} as const;

export interface CognitoError extends Error {
  code: string;
}

const formatEmailAddress = (email: string): string => email.toLowerCase();

let userPool: CognitoUserPool | null = null;
function getUserPool() {
  if (!userPool) {
    userPool = new CognitoUserPool({
      UserPoolId: Flags.COGNITO_USER_POOL_ID,
      ClientId: Flags.COGNITO_CLIENT_ID,
    });
  }
  return userPool;
}

export async function signUp(
  email: string,
  password: string,
  givenName: string,
  familyName: string,
): Promise<CognitoUser> {
  const formattedEmail = formatEmailAddress(email);

  const attributes = [
    new CognitoUserAttribute({
      Name: "email",
      Value: formattedEmail,
    }),
    new CognitoUserAttribute({
      Name: "given_name",
      Value: givenName,
    }),
    new CognitoUserAttribute({
      Name: "family_name",
      Value: familyName,
    }),
  ];

  return new Promise((resolve, reject) => {
    getUserPool().signUp(formattedEmail, password, attributes, [], (err, result) => {
      if (err || !result) {
        reject(err ?? new Error("Cognito signup returned undefined"));
        return;
      }

      resolve(result.user);
    });
  });
}

export async function confirm(email: string, code: string): Promise<void> {
  const formattedEmail = formatEmailAddress(email);
  let user = new CognitoUser({
    Username: formattedEmail,
    Pool: getUserPool(),
  });

  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (err) => {
      if (err) {
        // Try again with non-lowercased email for legacy accounts
        user = new CognitoUser({
          Username: email,
          Pool: getUserPool(),
        });

        user.confirmRegistration(code, true, (err2) => {
          if (err2) {
            reject(new Error(err2));
          } else {
            resolve();
          }
        });
      } else {
        resolve();
      }
    });
  });
}

export async function sendPasswordReset(email: string): Promise<void> {
  const formattedEmail = formatEmailAddress(email);
  let user = new CognitoUser({
    Username: formattedEmail,
    Pool: getUserPool(),
  });

  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: resolve,
      onFailure: () => {
        // Try again with non-lowercased email for legacy accounts
        user = new CognitoUser({
          Username: email,
          Pool: getUserPool(),
        });

        user.forgotPassword({
          onSuccess: resolve,
          onFailure: (err: Error) => reject(err),
        });
      },
    });
  });
}

export async function confirmPassword({
  email,
  confirmationCode,
  newPassword,
}: {
  email: string;
  confirmationCode: string;
  newPassword: string;
}) {
  const formattedEmail = formatEmailAddress(email);
  let user = new CognitoUser({
    Username: formattedEmail,
    Pool: getUserPool(),
  });

  return new Promise((resolve, reject) => {
    user.confirmPassword(confirmationCode, newPassword, {
      onSuccess: () => {
        resolve(null);
      },
      onFailure: () => {
        // Try again with non-lowercased email for legacy accounts
        user = new CognitoUser({
          Username: email,
          Pool: getUserPool(),
        });

        user.confirmPassword(confirmationCode, newPassword, {
          onSuccess: () => {
            resolve(null);
          },
          onFailure: reject,
        });
      },
    });
  });
}

export async function resendConfirmationCode(email: string): Promise<void> {
  const formattedEmail = formatEmailAddress(email);
  let user = new CognitoUser({
    Username: formattedEmail,
    Pool: getUserPool(),
  });

  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((e) => {
      const err = e as CognitoError;

      if (err) {
        // Try again with non-lowercased email for legacy accounts
        user = new CognitoUser({
          Username: email,
          Pool: getUserPool(),
        });

        user.resendConfirmationCode((e2) => {
          const err2 = e2 as CognitoError;

          if (err2) {
            reject(err2);
          } else {
            resolve();
          }
        });
      } else {
        resolve();
      }
    });
  });
}

export type PasswordChallengeFunc = (newPassword: string) => Promise<any>;

export async function logIn(email: string, password: string): Promise<CognitoUserSession> {
  const formattedEmail = formatEmailAddress(email);
  const authenticationDetails = new AuthenticationDetails({
    Username: formattedEmail,
    Password: password,
  });

  let user = new CognitoUser({
    Username: formattedEmail,
    Pool: getUserPool(),
  });

  return new Promise((resolve, reject) => {
    const newPasswordRequired = () => {
      reject({
        errorType: "NEW_PASSWORD_CHALLENGE",
        setNewPassword: async (newPassword: string) => {
          return new Promise((resolveInner, rejectInner) => {
            user.completeNewPasswordChallenge(
              newPassword,
              {},
              {
                onSuccess: resolveInner,
                onFailure: rejectInner,
              },
            );
          });
        },
      });
    };

    user.authenticateUser(authenticationDetails, {
      onSuccess: resolve,
      onFailure: () => {
        // Try again with non-lowercased email for legacy accounts
        user = new CognitoUser({
          Username: email,
          Pool: getUserPool(),
        });

        user.authenticateUser(authenticationDetails, {
          onSuccess: resolve,
          onFailure: reject,
          newPasswordRequired,
        });
      },
      newPasswordRequired,
    });
  });
}

export async function getSession(user: CognitoUser): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    if (user === null) {
      reject("You're not logged in.");
      return;
    }

    user.getSession((err, session) => {
      if (err || !session) {
        reject(err ?? new Error("getSession returned undefined"));
        return;
      }
      if (session.isValid() === false) {
        reject("Session is invalid");
      }
      resolve(session);
    });
  });
}

export async function isAuthenticated() {
  const user = getUserPool().getCurrentUser();
  return user ? getSession(user) : false;
}

export function getUser() {
  return getUserPool().getCurrentUser();
}

export async function logOut() {
  const user = getUserPool().getCurrentUser();
  if (!user) {
    return Promise.reject("Current user session not found");
  }
  return Promise.resolve(user.signOut());
}
